import React from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { useGlobalizationContext, useTranslation } from '@cs-cms/state-management';
import { PRIVATE_VIEW_NAME_MIN, PRIVATE_VIEW_NAME_MAX } from '@cs-cms/types';

interface RenameViewDialogProps {
  open: boolean;
  onClose: () => void;
  onRenameView: (viewId: number, newName: string) => void;
  currentViewId: number;
  currentViewName: string;
}

export const RenameViewDialog: React.FC<RenameViewDialogProps> = ({
  open,
  onClose,
  onRenameView,
  currentViewId,
  currentViewName
}) => {
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const [newViewName, setNewViewName] = React.useState<string>(currentViewName);
  const [viewNameError, setViewNameError] = React.useState<string>('');

  React.useEffect(() => {
    if (open) {
      setNewViewName(currentViewName);
      setViewNameError('');
    }
  }, [open, currentViewName]);

  const handleInputChange = (e: InputChangeEvent) => {
    const value = e.value;
    setNewViewName(value);
    if (value.length < PRIVATE_VIEW_NAME_MIN) {
      setViewNameError(translate('custom.settings.views.nameLength', { count: PRIVATE_VIEW_NAME_MIN }));
    } else if (value.length > PRIVATE_VIEW_NAME_MAX) {
      setViewNameError(translate('custom.settings.views.nameLengthMax', { count: PRIVATE_VIEW_NAME_MAX }));
    } else {
      setViewNameError('');
    }
  };

  const handleRenameView = () => {
    if (newViewName.length >= 2) {
      onRenameView(currentViewId, newViewName);
      onClose();
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleRenameView();
    }
  };

  return (
    <>
      {open && (
        <Dialog
          title={translate('custom.settings.views.dialog.renameTitle')}
          onClose={onClose}
          width={400}
          className="k-dialog-centered viewNameDialog"
        >
          <div className="k-form k-form-horizontal" onKeyDown={handleKeyPress}>
            <label className="k-label">{translate('custom.settings.views.dialog.viewNameLabel')}</label>
            <Input
              value={newViewName}
              onChange={handleInputChange}
              placeholder={translate('custom.settings.views.dialog.viewNamePlaceholder')}
            />
            {viewNameError && <div className="k-form-error k-text-error">{viewNameError}</div>}
          </div>

          <div className="k-actions k-actions-end k-actions-horizontal k-mt-4">
            <Button fillMode="flat" onClick={onClose}>
              {translate('custom.common.cancel')}
            </Button>
            <Button
              themeColor="primary"
              onClick={handleRenameView}
              disabled={
                newViewName.length < PRIVATE_VIEW_NAME_MIN ||
                newViewName.length > PRIVATE_VIEW_NAME_MAX ||
                newViewName === currentViewName
              }
            >
              {translate('custom.common.rename')}
            </Button>
          </div>
        </Dialog>
      )}
    </>
  );
};
